import { useQuery } from 'react-query'
import { AiClient } from 'api/clients/AiClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { usePollAICaptionGeneration } from '../aiTemplates/usePollAICaptionGeneration'
import { processToolCalls } from 'components/Design/CaptionTab/processToolCalls'

interface UseVisualStylePreviewCaptionProps {
  brandKitId?: number
  brandVoiceId?: number
  brandKitName?: string
  enabled?: boolean
}

const CACHE_CONFIG = {
  staleTime: Infinity,
  cacheTime: 1000 * 60 * 30,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
}

/**
 * Generates and caches a caption for previews.
 */
export const useVisualStylePreviewCaption = ({
  brandKitId,
  brandVoiceId,
  brandKitName,
  enabled = true,
}: UseVisualStylePreviewCaptionProps) => {
  const { data: jobId, isLoading: isGenerationLoading } = useQuery(
    ['visualStylePreviewCaption', 'jobId', brandKitId],
    async () => {
      const prompt = brandKitName ? `Create engaging content for ${brandKitName}` : 'Create engaging content'
      const response = await AiClient.captionGeneration({ prompt, brandVoiceId, language: 'English' })

      if (response.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Failed to start caption generation')
      return response.data
    },
    { ...CACHE_CONFIG, enabled: enabled && !!brandKitId },
  )

  const { data: captionData, isLoading: isPollingLoading } = usePollAICaptionGeneration(
    { id: jobId },
    { ...CACHE_CONFIG, enabled: !!jobId },
  )

  // Process and truncate caption
  const { data: caption, isLoading: isProcessingLoading } = useQuery(
    ['visualStylePreviewCaption', 'final', brandKitId],
    () => {
      if (captionData?.status !== 'done' || !captionData.data) return null

      const captions = processToolCalls(captionData.data)
      const text = captions?.[0] || ''

      if (text.length <= 90) return text
      const lastSpace = text.lastIndexOf(' ', 90)
      return text.substring(0, lastSpace > 0 ? lastSpace : 90).trim()
    },
    { ...CACHE_CONFIG, enabled: captionData?.status === 'done' },
  )

  const isGenerating =
    enabled && brandKitId && (isGenerationLoading || isPollingLoading || isProcessingLoading || !caption)

  return {
    caption: caption || undefined,
    isGenerating: !!isGenerating,
    error: captionData?.status === 'failed' ? new Error('Caption generation failed') : undefined,
  }
}
